import { ArrowDown, ArrowUp, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  BottomSheet,
  Button,
  Card,
  DropdownSelect,
  ListRow,
  NumPad,
  SegmentedControl,
  SwipeRow,
  useToast,
  type DropdownOption,
} from '../../components';
import { useActions, useAppData } from '../../data';
import type { QuickPreset, TransactionType } from '../../data/types';
import { formatCurrency } from '../../lib/currency';

const MAX = 8;

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Settings → Quick Add Presets manager. Lists existing presets with
 * swipe-to-delete and tap-to-edit. The Add button below disables when
 * the user hits the 8-preset cap.
 */
export function QuickPresetsSheet({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const [editing, setEditing] = useState<'add' | QuickPreset | null>(null);

  const presets = useMemo(
    () =>
      [...(data.profile.quickPresets ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [data.profile.quickPresets],
  );
  const atCap = presets.length >= MAX;
  const currency = data.profile.currency;

  return (
    <BottomSheet open={open} onClose={onClose} title="Quick Add Presets">
      <div className="px-screen-x py-3">
        <p className="text-[13px] text-text-secondary mb-3">
          Up to {MAX} one-tap shortcuts. Tap from the Today screen to add
          instantly.
        </p>

        {presets.length === 0 ? (
          <Card flush elevation="sm" className="mb-card-gap py-4">
            <p className="px-card-pad text-[13px] text-text-secondary">
              No presets yet. Tap "Add preset" to create one.
            </p>
          </Card>
        ) : (
          <Card flush elevation="sm" className="mb-card-gap py-1">
            {presets.map((p, i) => {
              const cat = data.categories.find((c) => c.id === p.categoryId);
              return (
                <div key={p.id}>
                  {i > 0 ? (
                    <div className="h-px bg-surface-secondary mx-card-pad" />
                  ) : null}
                  <SwipeRow
                    onDelete={() => {
                      actions.deleteQuickPreset(p.id);
                      toast.show({ message: 'Preset removed', kind: 'info' });
                    }}
                    onTap={() => setEditing(p)}
                  >
                    <ListRow
                      icon={
                        p.transactionType === 'income' ? (
                          <ArrowUp size={18} strokeWidth={1.75} />
                        ) : (
                          <ArrowDown size={18} strokeWidth={1.75} />
                        )
                      }
                      iconBgClass={
                        p.transactionType === 'income'
                          ? 'bg-positive/15'
                          : 'bg-negative/15'
                      }
                      iconColorClass={
                        p.transactionType === 'income'
                          ? 'text-positive'
                          : 'text-negative'
                      }
                      title={p.label}
                      subtitle={cat?.name ?? '—'}
                      value={formatCurrency(p.amount, { currency })}
                      accessory="chevron"
                    />
                  </SwipeRow>
                </div>
              );
            })}
          </Card>
        )}

        <div className="flex flex-col gap-1.5">
          <Button
            variant="primary"
            fullWidth
            disabled={atCap}
            leadingIcon={<Plus size={16} strokeWidth={1.5} />}
            onClick={() => setEditing('add')}
          >
            Add preset
          </Button>
          {atCap ? (
            <p className="text-center text-[12px] text-text-tertiary">
              Maximum reached
            </p>
          ) : null}
        </div>
      </div>

      <PresetEditSheet
        mode={editing}
        currency={currency}
        onClose={() => setEditing(null)}
        onSave={(payload) => {
          try {
            if (payload.mode === 'add') {
              actions.addQuickPreset(payload.input);
              toast.show({ message: 'Preset added', kind: 'success' });
            } else {
              actions.updateQuickPreset(payload.id, payload.patch);
              toast.show({ message: 'Preset updated', kind: 'success' });
            }
            setEditing(null);
          } catch (err) {
            toast.show({
              message: (err as Error).message,
              kind: 'error',
            });
          }
        }}
      />
    </BottomSheet>
  );
}

type EditPayload =
  | {
      mode: 'add';
      input: {
        label: string;
        amount: number;
        categoryId: string;
        transactionType: TransactionType;
      };
    }
  | { mode: 'edit'; id: string; patch: Partial<QuickPreset> };

type EditProps = {
  mode: 'add' | QuickPreset | null;
  currency: string;
  onClose: () => void;
  onSave: (payload: EditPayload) => void;
};

const TYPE_OPTIONS: { value: TransactionType; label: string }[] = [
  { value: 'expense', label: 'Expense' },
  { value: 'income', label: 'Income' },
];

function PresetEditSheet({ mode, currency, onClose, onSave }: EditProps) {
  const data = useAppData();
  const open = mode !== null;
  const editing = mode && mode !== 'add' ? mode : null;

  const [label, setLabel] = useState('');
  const [type, setType] = useState<TransactionType>('expense');
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState(0);
  const [amountOpen, setAmountOpen] = useState(false);

  const categoryOptions: DropdownOption[] = useMemo(
    () =>
      data.categories
        .filter((c) => c.type === type)
        .sort((a, b) => a.order - b.order)
        .map((c) => ({ value: c.id, label: c.name })),
    [data.categories, type],
  );

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setLabel(editing.label);
      setType(editing.transactionType);
      setCategoryId(editing.categoryId);
      setAmount(editing.amount);
    } else {
      setLabel('');
      setType('expense');
      setAmount(0);
      setCategoryId('');
    }
    setAmountOpen(false);
  }, [open, editing]);

  // Pin the dropdown to a valid option whenever the type flips.
  useEffect(() => {
    if (!open) return;
    if (
      categoryOptions.length > 0 &&
      !categoryOptions.find((o) => o.value === categoryId)
    ) {
      setCategoryId(categoryOptions[0]!.value);
    }
  }, [open, categoryOptions, categoryId]);

  const submit = () => {
    const trimmed = label.trim();
    if (!trimmed || amount <= 0 || !categoryId) return;
    if (editing) {
      onSave({
        mode: 'edit',
        id: editing.id,
        patch: {
          label: trimmed,
          amount,
          categoryId,
          transactionType: type,
        },
      });
    } else {
      onSave({
        mode: 'add',
        input: {
          label: trimmed,
          amount,
          categoryId,
          transactionType: type,
        },
      });
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={editing ? 'Edit preset' : 'New preset'}
    >
      <div className="px-screen-x py-4 flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Label
          </span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 24))}
            maxLength={24}
            placeholder="e.g. Coffee"
            className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] text-text-primary outline-none focus:ring-2 focus:ring-accent"
          />
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Type
          </span>
          <SegmentedControl
            options={TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Category
          </span>
          <DropdownSelect
            options={categoryOptions}
            value={categoryId}
            onChange={setCategoryId}
          />
        </div>

        <button
          type="button"
          onClick={() => setAmountOpen(true)}
          className="flex flex-col gap-1.5 text-left"
        >
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Amount
          </span>
          <span className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] font-mono tabular-nums text-text-primary">
            {formatCurrency(amount, { currency })}
          </span>
        </button>

        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!label.trim() || amount <= 0 || !categoryId}
          onClick={submit}
        >
          {editing ? 'Save' : 'Add preset'}
        </Button>
      </div>

      <BottomSheet
        open={amountOpen}
        onClose={() => setAmountOpen(false)}
        title="Amount"
      >
        <NumPad
          initialValue={amount}
          currency={currency}
          onDone={(v) => {
            setAmount(v);
            setAmountOpen(false);
          }}
        />
      </BottomSheet>
    </BottomSheet>
  );
}

